/**
 * Поля заявки площадки в мастере заказа по экологии (ecology-order-fields, файл отдела экологии
 * 28.09.2026: «У каждой площадки есть реквизит… он необязательный, написать рядом „можно не
 * заполнять, если количество не известно“»). Набор полей — из позиции справочника (`orderFields`):
 * число — поле ввода, выбор — список галочек, отходы — список «код ФККО + наименование» с
 * подсказками справочника (код необязателен), файлы — загрузка сразу, отдельно от черновика
 * (ecology-order-fields-files-fkko). Подсказка поля — под подписью.
 */
import type { EcoFieldFile, EcoOrderField } from '@/lib/api'
import { FkkoListField, type FkkoRow } from '@/ecology/fkko-list'
import { FieldFiles } from '@/ecology/field-files'
import { LabeledCheckbox } from '@/sout/labeled-checkbox'
import { Field, RequiredMark } from '@/design-system/components/primitives/field'
import { Input } from '@/design-system/components/primitives/input'

/** Значения полей одной площадки в форме: число — строкой (как ввели), выбор — варианты. */
export type OrderSiteFieldValues = Record<string, string | string[]>

export function OrderSiteFields({
  siteName,
  fields,
  values,
  errors,
  onChange,
  fkko,
  fkkoErrors,
  onFkko,
  files,
  filesBusy,
  onUploadFile,
  onDeleteFile,
}: {
  /** Название площадки — в подписях для скринридеров (полей одной услуги на странице несколько). */
  siteName: string
  fields: EcoOrderField[]
  values: OrderSiteFieldValues
  /** Ошибки по ключу поля. */
  errors: Record<string, string>
  onChange: (key: string, value: string | string[]) => void
  /** Отходы полей «список ФККО» по ключу и ошибки по «номер.code|name». */
  fkko: Record<string, FkkoRow[]>
  fkkoErrors: Record<string, Record<string, string>>
  onFkko: (key: string, rows: FkkoRow[]) => void
  /** Файлы файловых полей по ключу; `filesBusy` — идёт загрузка или удаление. */
  files: Record<string, EcoFieldFile[]>
  filesBusy: boolean
  onUploadFile: (key: string, file: File) => Promise<void>
  onDeleteFile: (key: string, fileId: string) => Promise<void>
}) {
  return (
    <div className="flex flex-col gap-3">
      {fields.map((f) =>
        f.type === 'fkko_list' ? (
          <FkkoListField
            key={f.key}
            field={f}
            rows={fkko[f.key] ?? []}
            errors={fkkoErrors[f.key] ?? {}}
            disabled={false}
            onChange={(rows) => onFkko(f.key, rows)}
          />
        ) : f.type === 'file_multi' ? (
          <FieldFiles
            key={f.key}
            field={f}
            files={files[f.key] ?? []}
            disabled={filesBusy}
            onUpload={(file) => onUploadFile(f.key, file)}
            onDelete={(fileId) => onDeleteFile(f.key, fileId)}
          />
        ) : f.type === 'number' ? (
          <Field
            key={f.key}
            label={f.label}
            hint={f.hint}
            required={f.required}
            error={errors[f.key]}
            className="mt-0"
          >
            {(props) => (
              <Input
                {...props}
                className="max-w-40"
                inputMode="numeric"
                aria-label={`${f.label} — ${siteName}`}
                value={typeof values[f.key] === 'string' ? (values[f.key] as string) : ''}
                invalid={Boolean(errors[f.key])}
                onChange={(e) => onChange(f.key, e.target.value)}
              />
            )}
          </Field>
        ) : (
          <fieldset key={f.key} className="min-w-0 border-0 p-0">
            <legend className="mb-1 text-13 text-txt-secondary">
              {f.label}
              {f.required ? <RequiredMark /> : null}
            </legend>
            {f.hint ? <p className="mb-2 text-12 text-txt-tertiary">{f.hint}</p> : null}
            <div
              className="flex flex-col gap-2"
              role="group"
              aria-label={`${f.label} — ${siteName}`}
            >
              {f.options.map((option) => {
                const picked = Array.isArray(values[f.key]) ? (values[f.key] as string[]) : []
                return (
                  <LabeledCheckbox
                    key={option}
                    align="start"
                    checked={picked.includes(option)}
                    onCheckedChange={(on) =>
                      onChange(
                        f.key,
                        // Порядок — как в списке поля.
                        f.options.filter((o) => (o === option ? on : picked.includes(o))),
                      )
                    }
                  >
                    {option}
                  </LabeledCheckbox>
                )
              })}
            </div>
            {errors[f.key] ? (
              <p role="alert" className="mt-1 text-12 text-red">
                {errors[f.key]}
              </p>
            ) : null}
          </fieldset>
        ),
      )}
    </div>
  )
}
